import {
  useCustomerQuery,
  useCustomerSession,
  UseCustomerSessionReturn,
} from '@graphcommerce/magento-customer/hooks'
import { Trans } from '@lingui/react'
import { NoSsr, SwipeableDrawer } from '@mui/material'
import { useContext, useEffect, useRef, useState } from 'react'
import { CustomerDocument } from '../../../graphql/Customer.gql'
import { useFormIsEmailAvailable } from '../../../hooks/Customer/useFormIsEmailAvailable'
import { AccountSignInUpForm } from '../../CustomerAccount/AccountSignInUpForm/AccountSignInUpForm'
import { globalContext } from '../../NextUi/globalContext'
import { AccountMenu } from './AccountMenu'

type CustomerFabContentProps = {
  session?: UseCustomerSessionReturn
}

function CustomerFabContent(props: CustomerFabContentProps) {
  const { session } = props
  const { drawer, setDrawer } = useContext(globalContext)
  const ref = useRef<HTMLButtonElement>(null)
  const dashboard = useCustomerQuery(CustomerDocument, {
    fetchPolicy: 'cache-and-network',
  })
  const customer = dashboard.data?.customer
  const customerInitials = `${customer?.firstname?.trimStart().charAt(0)}${customer?.lastname
    ?.trimStart()
    .charAt(0)}`

  const customerName = customer ? `${customer?.firstname} ${customer?.lastname}` : ''
  const [showMenu, setShowMenu] = useState<boolean>(false)
  const [defaultMode, setDefaultMode] = useState<
    'email' | 'signin' | 'signup' | 'signedin' | 'session-expired' | 'forgot'
  >('signin')

  const customerQuery = useCustomerQuery(CustomerDocument)

  const { email } = customerQuery.data?.customer ?? {}
  const { setMode } = useFormIsEmailAvailable({
    email,
  })

  useEffect(() => {
    if (ref.current) {
      ref?.current?.blur()
    }
  }, [session?.loggedIn])

  if (session?.loggedIn) {
    return (
      <div className='relative'>
        <button type='button' onClick={() => setShowMenu((previous) => !previous)} ref={ref}>
          <div className='relative flex h-[35px] w-[35px] items-center justify-center rounded-[50%] bg-medium-gray hover:bg-70-grey'>
            {customer && customerInitials && (
              <span className='Type-XL-Medium text-pure-white'>{customerInitials}</span>
            )}
          </div>
        </button>
        <AccountMenu
          accountName={customerName}
          accountEmail={customer?.email}
          setShowMenu={setShowMenu}
          showMenu={showMenu}
        />
      </div>
    )
  }

  return (
    <div className='relative'>
      {/* Mobile: person icon */}
      <button
        type='button'
        className='flex items-center justify-center md:hidden'
        aria-label='Sign in'
        onClick={() => {
          setMode('signin')
          setDefaultMode('signin')
          setDrawer({ id: drawer.id === 'auth' ? null : 'auth' })
        }}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 24 24'
          fill='currentColor'
          className='h-6 w-6 text-black'
        >
          <path d='M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z' />
        </svg>
      </button>
      {/* Desktop: text buttons */}
      <div className='hidden items-center justify-center gap-2 md:flex'>
        <button
          type='button'
          onClick={() => {
            setMode('signin')
            setDefaultMode('signin')
            setDrawer({ id: drawer.id === 'auth' ? null : 'auth' })
          }}
        >
          <p className='Type-XL-Regular whitespace-nowrap text-black hover:text-bright-templi-purple'>
            <Trans id='Log in' />
          </p>
        </button>
        <span>|</span>
        <button
          type='button'
          onClick={() => {
            setMode('signup')
            setDefaultMode('signup')
            setDrawer({ id: drawer.id === 'auth' ? null : 'auth' })
          }}
        >
          <p className='Type-XL-Regular whitespace-nowrap text-black hover:text-bright-templi-purple'>
            <Trans id='Sign up' />
          </p>
        </button>
      </div>

      <SwipeableDrawer
        anchor='right'
        onClose={() => setDrawer({ id: null })}
        onOpen={() => {}}
        open={drawer.id === 'auth'}
        classes={{
          paper: 'shadow-m w-full max-w-full md:max-w-[520px]',
        }}
      >
        <AccountSignInUpForm onClose={() => setDrawer({ id: null })} defaultMode={defaultMode} />
      </SwipeableDrawer>
    </div>
  )
}

export type CustomerFabProps = Omit<CustomerFabContentProps, 'session'>

export function CustomerFab(props: CustomerFabProps) {
  const session = useCustomerSession()

  return (
    <NoSsr fallback={<CustomerFabContent {...props} />}>
      <CustomerFabContent session={session} {...props} />
    </NoSsr>
  )
}
